function normalizeUrl(value) {
  return String(value || '').trim().replace(/\/+$/, '');
}

function normalizeBackendRoot(value) {
  const raw = normalizeUrl(value);
  if (!raw) {
    return '';
  }

  try {
    const parsed = new URL(raw);
    const pathname = parsed.pathname.replace(/\/+$/, '').replace(/\/api$/i, '') || '/';
    parsed.pathname = pathname;
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return raw.replace(/\/api$/i, '');
  }
}

export function resolveAgendaLinks() {
  return {
    frontendRoot: normalizeUrl(window.location.origin),
    backendRoot: normalizeBackendRoot(process.env.REACT_APP_URL)
  };
}

export async function getLegacyInstanceRegistrationStatus() {
  const { frontendRoot, backendRoot } = resolveAgendaLinks();

  const query = new URLSearchParams({ frontendRoot, backendRoot }).toString();

  let response;
  try {
    response = await fetch(`${process.env.REACT_APP_URL}/controller/register-status?${query}`, {
      method: 'GET'
    });
  } catch (error) {
    return {
      registered: false,
      unavailable: true,
      instance: null,
      instanceId: null,
      source: 'status-check-network-error',
      controllerBaseUrl: process.env.REACT_APP_URL || null,
      frontendRoot,
      backendRoot,
      error: error?.message || 'Status check unavailable'
    };
  }

  const text = await response.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 404) {
      return {
        registered: false,
        unavailable: true,
        instance: null,
        instanceId: null,
        source: 'status-check-unavailable',
        controllerBaseUrl: process.env.REACT_APP_URL || null,
        frontendRoot,
        backendRoot,
        error: payload?.message || text || `Status check failed with status ${response.status}`
      };
    }

    throw new Error(payload?.message || text || `Status check failed with status ${response.status}`);
  }

  const registered = Boolean(payload?.registered);

  return {
    registered,
    instance: payload?.instance || (payload?.instanceId ? { instanceId: payload.instanceId } : null),
    instanceId: payload?.instanceId || payload?.instance?.instanceId || null,
    source: payload?.source || null,
    controllerBaseUrl: payload?.controllerBaseUrl || null,
    frontendRoot,
    backendRoot
  };
}

export async function registerLegacyInstanceInController() {
  const { frontendRoot, backendRoot } = resolveAgendaLinks();

  const response = await fetch(`${process.env.REACT_APP_URL}/controller/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ frontendRoot, backendRoot })
  });

  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || `Registration failed with status ${response.status}`);
  }

  return {
    message: text
      ? `${text} (frontend: ${frontendRoot}, backend: ${backendRoot || 'unknown'})`
      : `registered (frontend: ${frontendRoot}, backend: ${backendRoot || 'unknown'})`,
    frontendRoot,
    backendRoot
  };
}

export async function ensureLegacyInstanceRegistered() {
  const status = await getLegacyInstanceRegistrationStatus();
  if (status?.unavailable) {
    return {
      registered: false,
      skipped: true,
      unavailable: true,
      source: status.source || 'status-check-unavailable',
      instance: null,
      frontendRoot: status.frontendRoot,
      backendRoot: status.backendRoot,
      message: status.error || 'Registration status endpoint unavailable'
    };
  }

  if (status?.registered) {
    return {
      registered: true,
      skipped: true,
      source: 'controller-status',
      instance: status.instance || null,
      frontendRoot: status.frontendRoot,
      backendRoot: status.backendRoot
    };
  }

  try {
    const registration = await registerLegacyInstanceInController();
    return {
      registered: true,
      skipped: false,
      instance: null,
      frontendRoot: registration.frontendRoot,
      backendRoot: registration.backendRoot,
      message: registration.message
    };
  } catch (error) {
    return {
      registered: false,
      skipped: false,
      unavailable: false,
      source: 'auto-registration-failed',
      instance: null,
      frontendRoot: status.frontendRoot,
      backendRoot: status.backendRoot,
      message: error?.message || 'Automatic registration failed'
    };
  }
}